import { defaultsDeep } from 'es-toolkit/compat';

import config from 'configs/app';

const DEFAULT_THEME_COLORS = {
  bg: {
    primary: {
      // for some reason links to colors.white and colors.black variables are not working here
      // so we use hex values instead
      // but it is not the case for other colors
      _light: { value: '#FFFFFF' }, // colors.white
      _dark: { value: '#101112' }, // colors.black
    },
  },
  text: {
    primary: {
      _light: { value: '{colors.blackAlpha.800}' },
      _dark: { value: '{colors.whiteAlpha.800}' },
    },
    secondary: {
      _light: { value: '{colors.gray.500}' },
      _dark: { value: '{colors.gray.400}' },
    },
  },
  hover: {
    _light: { value: '{colors.blue.400}' },
    _dark: { value: '{colors.blue.400}' },
  },
  selected: {
    control: {
      text: {
        _light: { value: '#0A0A0F' },
        _dark: { value: '#0A0A0F' },
      },
      bg: {
        _light: { value: '#D4AF37' },
        _dark: { value: '#D4AF37' },
      },
    },
    option: {
      bg: {
        _light: { value: '{colors.blue.500}' },
        _dark: { value: '{colors.blue.500}' },
      },
    },
  },
  icon: {
    primary: {
      _light: { value: '{colors.gray.500}' },
      _dark: { value: '{colors.gray.400}' },
    },
    secondary: {
      _light: { value: '{colors.gray.400}' },
      _dark: { value: '{colors.gray.500}' },
    },
  },
  button: {
    primary: {
      _light: { value: '{colors.blue.600}' },
      _dark: { value: '{colors.blue.600}' },
    },
  },
  link: {
    primary: {
      _light: { value: '{colors.blue.600}' },
      _dark: { value: '{colors.blue.300}' },
    },
  },
  graph: {
    line: {
      _light: { value: '{colors.blue.500}' },
      _dark: { value: '{colors.blue.200}' },
    },
    gradient: {
      start: {
        _light: { value: 'rgba(144, 205, 244, 0.3)' }, // blue.200 with opacity 0.3
        _dark: { value: 'rgba(144, 205, 244, 0.3)' }, // blue.200 with opacity 0.3
      },
      stop: {
        _light: { value: 'rgba(144, 205, 244, 0)' }, // blue.200 with opacity 0
        _dark: { value: 'rgba(144, 205, 244, 0)' }, // blue.200 with opacity 0
      },
    },
  },
  navigation: {
    bg: {
      selected: {
        _light: { value: '{colors.blue.50}' },
        _dark: { value: '{colors.gray.800}' },
      },
    },
    text: {
      selected: {
        _light: { value: '{colors.blue.700}' },
        _dark: { value: '{colors.gray.50}' },
      },
    },
  },
  stats: {
    bg: {
      _light: { value: '{colors.gray.50}' },
      _dark: { value: '{colors.whiteAlpha.100}' },
    },
  },
  topbar: {
    bg: {
      _light: { value: '{colors.gray.50}' },
      _dark: { value: '{colors.whiteAlpha.100}' },
    },
  },
  tabs: {
    text: {
      primary: {
        _light: { value: '{colors.gray.600}' },
        _dark: { value: '{colors.gray.300}' },
      },
    },
  },
};

const colors = {
  // BASE COLORS
  green: {
    '50': { value: '#E8F5E9' },
    '100': { value: '#C8E6C9' },
    '200': { value: '#A5D6A7' },
    '300': { value: '#81C784' },
    '400': { value: '#66BB6A' },
    '500': { value: '#4CAF50' },
    '600': { value: '#388E3C' },
    '700': { value: '#2E7D32' },
    '800': { value: '#1B5E20' },
    '900': { value: '#145214' },
  },
  blue: {
    // 泉链 / Triquan brand ramp — champagne/rose-gold from the diamond mark
    '50': { value: '#FBF3DC' },
    '100': { value: '#F5E4B0' },
    '200': { value: '#EED484' },
    '300': { value: '#E6C55B' },
    '400': { value: '#DDB944' },
    '500': { value: '#D4AF37' }, // accent / links — signature gold
    '600': { value: '#B8941F' }, // primary buttons — deep gold
    '700': { value: '#9C7E1F' },
    '800': { value: '#7D6418' },
    '900': { value: '#5E4B12' },
  },
  red: {
    '50': { value: '#FFF5F5' },
    '100': { value: '#FED7D7' },
    '200': { value: '#FEB2B2' },
    '300': { value: '#FC8181' },
    '400': { value: '#F56565' },
    '500': { value: '#E53E3E' },
    '600': { value: '#C53030' },
    '700': { value: '#9B2C2C' },
    '800': { value: '#822727' },
    '900': { value: '#63171B' },
  },
  orange: {
    '50': { value: '#FFFAF0' },
    '100': { value: '#FEEBCB' },
    '200': { value: '#FBD38D' },
    '300': { value: '#F6AD55' },
    '400': { value: '#ED8936' },
    '500': { value: '#DD6B20' },
    '600': { value: '#C05621' },
    '700': { value: '#9C4221' },
    '800': { value: '#7B341E' },
    '900': { value: '#652B19' },
  },
  yellow: {
    '50': { value: '#FFFFF0' },
    '100': { value: '#FEFCBF' },
    '200': { value: '#FAF089' },
    '300': { value: '#F6E05E' },
    '400': { value: '#ECC94B' },
    '500': { value: '#D69E2E' },
    '600': { value: '#B7791F' },
    '700': { value: '#975A16' },
    '800': { value: '#744210' },
    '900': { value: '#5F370E' },
  },
  gray: {
    '50': { value: '#F7FAFC' },
    '100': { value: '#EDF2F7' },
    '200': { value: '#E2E8F0' },
    '300': { value: '#CBD5E0' },
    '400': { value: '#A0AEC0' },
    '500': { value: '#718096' },
    '600': { value: '#4A5568' },
    '700': { value: '#2D3748' },
    '800': { value: '#1A202C' },
    '900': { value: '#171923' },
  },
  teal: {
    '50': { value: '#E0F2F1' },
    '100': { value: '#B2DFDB' },
    '200': { value: '#80CBC4' },
    '300': { value: '#4DB6AC' },
    '400': { value: '#26A69A' },
    '500': { value: '#009688' },
    '600': { value: '#00796B' },
    '700': { value: '#00695C' },
    '800': { value: '#004D40' },
    '900': { value: '#00332A' },
  },
  cyan: {
    '50': { value: '#E0F7FA' },
    '100': { value: '#B2EBF2' },
    '200': { value: '#80DEEA' },
    '300': { value: '#4DD0E1' },
    '400': { value: '#26C6DA' },
    '500': { value: '#00BCD4' },
    '600': { value: '#00ACC1' },
    '700': { value: '#0097A7' },
    '800': { value: '#00838F' },
    '900': { value: '#006064' },
  },
  purple: {
    '50': { value: '#FAF5FF' },
    '100': { value: '#E9D8FD' },
    '200': { value: '#D6BCFA' },
    '300': { value: '#B794F4' },
    '400': { value: '#9F7AEA' },
    '500': { value: '#805AD5' },
    '600': { value: '#6B46C1' },
    '700': { value: '#553C9A' },
    '800': { value: '#44337A' },
    '900': { value: '#322659' },
  },
  pink: {
    '50': { value: '#FFF5F7' },
    '100': { value: '#FED7E2' },
    '200': { value: '#FBB6CE' },
    '300': { value: '#F687B3' },
    '400': { value: '#ED64A6' },
    '500': { value: '#D53F8C' },
    '600': { value: '#B83280' },
    '700': { value: '#97266D' },
    '800': { value: '#702459' },
    '900': { value: '#521B41' },
  },
  black: { value: '#101112' },
  white: { value: '#ffffff' },
  whiteAlpha: {
    '50': { value: 'RGBA(255, 255, 255, 0.04)' },
    '100': { value: 'RGBA(255, 255, 255, 0.06)' },
    '200': { value: 'RGBA(255, 255, 255, 0.08)' },
    '300': { value: 'RGBA(255, 255, 255, 0.16)' },
    '400': { value: 'RGBA(255, 255, 255, 0.24)' },
    '500': { value: 'RGBA(255, 255, 255, 0.36)' },
    '600': { value: 'RGBA(255, 255, 255, 0.48)' },
    '700': { value: 'RGBA(255, 255, 255, 0.64)' },
    '800': { value: 'RGBA(255, 255, 255, 0.80)' },
    '900': { value: 'RGBA(255, 255, 255, 0.92)' },
  },
  blackAlpha: {
    '50': { value: 'RGBA(16, 17, 18, 0.04)' },
    '100': { value: 'RGBA(16, 17, 18, 0.06)' },
    '200': { value: 'RGBA(16, 17, 18, 0.08)' },
    '300': { value: 'RGBA(16, 17, 18, 0.16)' },
    '400': { value: 'RGBA(16, 17, 18, 0.24)' },
    '500': { value: 'RGBA(16, 17, 18, 0.36)' },
    '600': { value: 'RGBA(16, 17, 18, 0.48)' },
    '700': { value: 'RGBA(16, 17, 18, 0.64)' },
    '800': { value: 'RGBA(16, 17, 18, 0.80)' },
    '900': { value: 'RGBA(16, 17, 18, 0.92)' },
  },

  // BRAND COLORS
  github: { value: '#171923' },
  telegram: { value: '#2775CA' },
  linkedin: { value: '#1564BA' },
  discord: { value: '#9747FF' },
  slack: { value: '#1BA27A' },
  twitter: { value: '#000000' },
  opensea: { value: '#2081E2' },
  facebook: { value: '#4460A0' },
  medium: { value: '#231F20' },
  reddit: { value: '#FF4500' },
  celo: { value: '#FCFF52' },
  clusters: { value: '#DE6061' },

  // THEME COLORS
  theme: defaultsDeep(config.UI.colorTheme.overrides, DEFAULT_THEME_COLORS),
};

export default colors;
